import { useCallback, useRef, useState } from 'react'
import { CgSpinner } from 'react-icons/cg'
import { FiImage, FiUpload, FiX } from 'react-icons/fi'

import { Button } from '~/components/Button'
import { uploadPersonalTrainerLogo } from '~/modules/personalTrainer/services'
import { insertToast } from '~/modules/toasts/services'

interface LogoInputProps {
  personalTrainerId: string
  currentLogoUrl?: string
  onUploadSuccess?: (logoUrl: string) => void
  className?: string
}

const MAX_FILE_SIZE = 5 * 1024 * 1024 // 5MB
const ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp']
const DEFAULT_LOGO_URL = 'https://gpa-bucket.s3.amazonaws.com/gpaLogo.png'

export const LogoInput: React.FC<LogoInputProps> = ({
  personalTrainerId,
  currentLogoUrl,
  onUploadSuccess,
  className = '',
}) => {
  const [preview, setPreview] = useState<string | null>(null)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [uploading, setUploading] = useState(false)
  const [logoError, setLogoError] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const constructLogoUrl = useCallback(() => {
    if (currentLogoUrl) return currentLogoUrl
    return `https://gpa-bucket.s3.amazonaws.com/logo-${personalTrainerId}`
  }, [personalTrainerId, currentLogoUrl])

  const handleFileSelect = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0]
      if (!file) return

      // Validate file type
      if (!ALLOWED_TYPES.includes(file.type)) {
        insertToast({
          title: 'Formato inválido',
          message: 'Use apenas arquivos JPEG, JPG, PNG ou WebP',
          duration: 3,
          type: 'error',
        })
        return
      }

      // Validate file size
      if (file.size > MAX_FILE_SIZE) {
        insertToast({
          title: 'Arquivo muito grande',
          message: 'O tamanho máximo é 5MB',
          duration: 3,
          type: 'error',
        })
        return
      }

      setSelectedFile(file)

      // Create preview
      const reader = new FileReader()
      reader.onloadend = () => {
        setPreview(reader.result as string)
      }
      reader.readAsDataURL(file)
    },
    [],
  )

  const handleUpload = useCallback(async () => {
    if (!selectedFile) return

    setUploading(true)
    try {
      const result = await uploadPersonalTrainerLogo(selectedFile)
      if (result?.logoUrl) {
        insertToast({
          title: 'Sucesso!',
          message: 'Logo enviado com sucesso',
          duration: 3,
          type: 'success',
        })
        setPreview(null)
        setSelectedFile(null)
        setLogoError(false)
        onUploadSuccess?.(result.logoUrl)
      }
    } catch (error) {
      insertToast({
        title: 'Erro ao enviar',
        message: 'Não foi possível enviar o logo',
        duration: 3,
        type: 'error',
      })
    } finally {
      setUploading(false)
    }
  }, [selectedFile, onUploadSuccess])

  const handleRemoveSelection = useCallback(() => {
    setSelectedFile(null)
    setPreview(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }, [])

  const handleLogoError = useCallback(() => {
    setLogoError(true)
  }, [])

  const displayLogoUrl = preview || (logoError ? DEFAULT_LOGO_URL : constructLogoUrl())

  return (
    <div className={`flex flex-col gap-4 ${className}`}>
      <div className='flex flex-col gap-2'>
        <span className='font-semibold text-gpa-gray-800'>Logo do Personal Trainer</span>
        <span className='text-sm text-gpa-gray-600'>
          Formatos aceitos: JPEG, PNG, WebP (máx. 5MB)
        </span>
      </div>

      {/* Logo Preview */}
      <div className='flex items-center justify-center w-full'>
        <div className='relative w-48 h-48 border-2 border-dashed border-gpa-gray-300 rounded-lg overflow-hidden bg-gpa-gray-50 flex items-center justify-center'>
          {displayLogoUrl ? (
            <img
              src={displayLogoUrl}
              alt='Logo preview'
              className='w-full h-full object-contain'
              onError={handleLogoError}
            />
          ) : (
            <FiImage size={64} className='text-gpa-gray-400' />
          )}
        </div>
      </div>

      {/* File Selection */}
      <div className='flex flex-col gap-3'>
        <input
          ref={fileInputRef}
          type='file'
          accept='image/jpeg,image/jpg,image/png,image/webp'
          onChange={handleFileSelect}
          className='hidden'
          disabled={uploading}
        />

        {selectedFile ? (
          <div className='flex flex-col gap-2'>
            <div className='flex items-center justify-between p-3 bg-gpa-blue-50 rounded-lg border border-gpa-blue-200'>
              <div className='flex items-center gap-2 flex-1 min-w-0'>
                <FiImage size={20} className='text-gpa-blue-500 flex-shrink-0' />
                <span className='text-sm text-gpa-gray-800 truncate'>
                  {selectedFile.name}
                </span>
                <span className='text-xs text-gpa-gray-600 flex-shrink-0'>
                  ({(selectedFile.size / 1024).toFixed(0)} KB)
                </span>
              </div>
              {!uploading && (
                <button
                  type='button'
                  onClick={handleRemoveSelection}
                  className='ml-2 p-1 hover:bg-gpa-blue-100 rounded transition-colors'
                >
                  <FiX size={20} className='text-gpa-gray-600' />
                </button>
              )}
            </div>

            <Button
              type='button'
              mode='filled'
              onClick={handleUpload}
              disabled={uploading}
              className='w-full'
            >
              {uploading ? (
                <div className='flex items-center gap-2'>
                  <CgSpinner size={20} className='animate-spin' />
                  <span>Enviando...</span>
                </div>
              ) : (
                <div className='flex items-center gap-2'>
                  <FiUpload size={20} />
                  <span>Enviar Logo</span>
                </div>
              )}
            </Button>
          </div>
        ) : (
          <Button
            type='button'
            mode='outlined'
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className='w-full'
          >
            <div className='flex items-center gap-2'>
              <FiUpload size={20} />
              <span>Selecionar Arquivo</span>
            </div>
          </Button>
        )}
      </div>
    </div>
  )
}
